import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Platform } from 'react-native';

import { IUserGeoQuery, UserGeoDocument } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { initLaunchDarklyPromise } from 'state/launchdarkly/launchdarkly-core';
import { isProduction } from 'utils/environment';
import logger from 'utils/logger';

import {
  AgeAssuranceBlockedReason,
  AgeAssuranceGateState,
  AgeAssuranceMode,
  AgeAssuranceTelemetryEvent,
  AgeAssuranceTerminalEvent,
  UserGeo,
} from '../types';
import {
  resolveAgeAssuranceMode,
  selectAgeAssuranceDependencies,
} from '../utils/age-assurance-mode';
import { requestPlatformAgeAssurance } from '../utils/age-range-adapter';
import { processAgeAssuranceCoordinator } from '../utils/coordinator';

const USER_GEO_TIMEOUT_MS = 3000;
const LAUNCH_DARKLY_STARTUP_BOUND_MS = 3000;

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => reject(new Error('age-assurance-timeout')), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
};

export const useAgeAssurance = (): {
  state: AgeAssuranceGateState;
  blockedReason: AgeAssuranceBlockedReason | null;
  retry: () => Promise<AgeAssuranceGateState>;
} => {
  const client = useApolloClient();
  const flagValue = useFlag(LaunchDarklyFlag.ENABLE_AGE_ASSURANCE);
  const mode = resolveAgeAssuranceMode(flagValue, isProduction);
  const enabled = mode !== AgeAssuranceMode.OFF;
  const [snapshot, setSnapshot] = useState(processAgeAssuranceCoordinator.getSnapshot);
  const [isLaunchDarklySettled, setIsLaunchDarklySettled] = useState(Platform.OS === 'web');

  const liveDependencies = useMemo(
    () => ({
      queryUserGeo: async (): Promise<UserGeo | null> => {
        const result = await withTimeout(
          client.query<IUserGeoQuery>({
            query: UserGeoDocument,
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          }),
          USER_GEO_TIMEOUT_MS
        );

        if (result.errors?.length || !result.data?.userGeo) {
          return null;
        }

        return {
          country: result.data.userGeo.country,
          state: result.data.userGeo.state,
        };
      },
      requestPlatformAgeAssurance,
      logTerminalDecision: (event: AgeAssuranceTerminalEvent) => {
        const telemetryEvent: AgeAssuranceTelemetryEvent = {
          ...event,
          ageAssuranceMode: mode,
        };
        logger.info(telemetryEvent);
      },
    }),
    [client, mode]
  );
  const dependencies = useMemo(() => selectAgeAssuranceDependencies(mode, liveDependencies), [
    liveDependencies,
    mode,
  ]);

  useEffect(() => processAgeAssuranceCoordinator.subscribe(setSnapshot), []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    let mounted = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const boundedLaunchDarklyWait = Promise.race([
      initLaunchDarklyPromise,
      new Promise<void>(resolve => {
        timeout = setTimeout(resolve, LAUNCH_DARKLY_STARTUP_BOUND_MS);
      }),
    ]);

    void boundedLaunchDarklyWait
      .catch(() => undefined)
      .finally(() => {
        if (timeout) {
          clearTimeout(timeout);
        }
        if (mounted) {
          setIsLaunchDarklySettled(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isLaunchDarklySettled && Platform.OS !== 'web') {
      return;
    }

    void processAgeAssuranceCoordinator.start({
      ...dependencies,
      enabled,
      isWeb: Platform.OS === 'web',
    });
  }, [dependencies, enabled, isLaunchDarklySettled]);

  const retry = useCallback(
    () => processAgeAssuranceCoordinator.retry({ ...dependencies, enabled }),
    [dependencies, enabled]
  );

  return {
    state: Platform.OS === 'web' && snapshot.state === 'pending' ? 'allowed' : snapshot.state,
    blockedReason: snapshot.blockedReason,
    retry,
  };
};
